import React, {Fragment, useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, Button, Flex, Text, useBreakpointValue, useMultiStyleConfig} from '@chakra-ui/react'
import {Link as RouteLink} from 'react-router-dom'
import {getGlobalLink} from '../../../utils/contentstack-utils'
import {useGetCategoriesData} from '../../../custom-api/hooks/use-get-categories-data'
import Iconbox from './iconbox'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

const SectionIconbox = ({title, cards, button, productRegistrationLink}) => {
    const variant = useBreakpointValue(
        {
            base: 'multiple',
            md: 'single',
        },
        {
            fallback: 'md',
        },
    )
    const {locale} = useMultiSite()

    const categoriesData = useGetCategoriesData()
    const styles = useMultiStyleConfig('Iconbox')

    const urlLocale = locale?.alias || locale?.id?.toLowerCase()
    const {url, label, isAbsolute} = getGlobalLink(button, categoriesData, urlLocale)

    const buttonProps = isAbsolute
        ? {
              as: 'a',
              href: url,
              target: '_blank',
              rel: 'noopener noreferrer',
          }
        : {
              as: RouteLink,
              to: url || `/${urlLocale}${productRegistrationLink}`,
          }

    const cardGroups = useMemo(() => {
        if (!cards?.length) return []

        if (variant === 'single') {
            return [cards]
        }

        return cards.reduce((groups, card) => {
            const currentGroup = groups[groups.length - 1]

            if (!currentGroup || currentGroup.length === 2) {
                groups.push([card])
            } else {
                currentGroup.push(card)
            }

            return groups
        }, [])
    }, [variant, cards])

    return (
        <Flex {...styles.sectionContainer}>
            <Text {...styles.sectionTitle}>{title}</Text>
            <Flex {...styles.cardsWrapper}>
                {cardGroups.map((group, groupIndex) => {
                    return (
                        <Flex key={groupIndex} {...styles.cardsRow} data-testid="card-group">
                            {group.map((card, index) => {
                                const {url, label, isAbsolute} = getGlobalLink(
                                    card?.button,
                                    categoriesData,
                                )
                                return (
                                    <Fragment key={card.title}>
                                        <Iconbox
                                            iconUrl={card.icon?.url}
                                            title={card.title}
                                            subtitle={card.subtitle}
                                            ctaLabel={label ?? card.button_label}
                                            ctaUrl={url}
                                            isAbsolute={isAbsolute}
                                            isLiveChat={card.isLiveChat}
                                        />
                                        {(index < group.length - 1 ||
                                            (group.length === 1 && variant === 'multiple')) && (
                                            <Flex {...styles.separator} />
                                        )}
                                        {group.length === 1 && variant === 'multiple' && (
                                            <Box {...styles.emptySpace} />
                                        )}
                                    </Fragment>
                                )
                            })}
                        </Flex>
                    )
                })}
            </Flex>
            {label && (
                <Button
                    {...buttonProps}
                    variant="primary"
                    {...styles.actionButton}
                    data-testid="button"
                >
                    {label}
                </Button>
            )}
        </Flex>
    )
}

SectionIconbox.displayName = 'SectionIconbox'

SectionIconbox.propTypes = {
    title: PropTypes.string,
    cards: PropTypes.array,
    button: PropTypes.object,
    productRegistrationLink: PropTypes.string,
}

export default SectionIconbox
